/**
 * The current UI language as a plain module value (ADR 0175).
 *
 * `lib/format.ts` and any other non-React formatter needs the active locale, and
 * a hook cannot reach them. LocaleProvider writes here whenever the language
 * changes, so date and number output follows the toggle without every call site
 * having to be a component.
 *
 * No `"use client"` — a plain module, importable from anywhere.
 */

import { setYearOffset } from "./date-core";

export type Locale = "th" | "en";

export const DEFAULT_LOCALE: Locale = "th";

let value: Locale = DEFAULT_LOCALE;

export function setDisplayLocale(l: Locale): void {
  value = l;
}

export function getDisplayLocale(): Locale {
  return value;
}

/**
 * BCP-47 tag for month and weekday names.
 *
 * `en-GB` rather than `en-US` because it is what most of this codebase already
 * passed (25 sites against 6) and because day-month order matches how the Thai
 * side reads dates — an English user and a Thai user looking at the same screen
 * should not see the numbers in a different order.
 */
export function displayLocaleTag(): string {
  return value === "en" ? "en-GB" : thaiDateTag();
}

/**
 * The year the organisation reads its dates in — ค.ศ. (2026, the default) or พ.ศ. (2569) — set on the settings page
 * (`settings.year_era`) and applied by the dashboard layout before the page renders. Thai screens follow it; English
 * ones stay ค.ศ. (a "2569" in English reads as a typo). Machine values (YYYY-MM-DD, file names, running numbers)
 * never do.
 */
export type YearEra = "ce" | "be";
let era: YearEra = "ce";

export function setYearEra(e: YearEra): void {
  era = e;
  setYearOffset(e === "be" ? 543 : 0);
}

export function getYearEra(): YearEra {
  return era;
}

/** an unset or unknown value reads as ค.ศ. */
export const resolveYearEra = (v: unknown): YearEra => (v === "be" ? "be" : "ce");

/**
 * Thai names in the organisation's calendar — for a date always written in Thai. `th-TH` alone makes Intl count
 * Buddhist years whatever the organisation chose, so the calendar is always named.
 */
export function thaiDateTag(): string {
  return `th-TH-u-ca-${era === "be" ? "buddhist" : "gregory"}`;
}

/** a Gregorian year as this screen shows it (a year printed by hand, not through Intl) */
export function displayYear(y: number): number {
  return value === "th" && era === "be" ? y + 543 : y;
}

const SHORT_MONTHS: Record<Locale, readonly string[]> = {
  th: ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."], // i18n-ok: the Thai list
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

/** "ต.ค." / "Oct" for any `YYYY-MM…` string, in the current UI language (the value as given when it is not one) */
export function shortMonthName(yyyyMm: string): string {
  return SHORT_MONTHS[value][Number(yyyyMm.slice(5, 7)) - 1] ?? yyyyMm;
}
